import { useState } from 'react';
import { PermissionAwareInput } from '../../../contexts/PermissionAccessContext';
import type { RestrictionTypeLookup } from '../../../types/safetySystem';
import type { ChildRow } from './ticketForm';
import { ENDORSEMENTS, isZeroDate } from './unitForm';
import { DateField, str } from './unitFields';
import { UnitChildGrid } from './UnitChildGrid';

/**
 * Cert-tab pieces for the Unit drawer (`UnitCertTab`): the V1 `@cert_layout` endorsement
 * rows (checkbox enables the expiry date) and the per-card-type restriction grids over the
 * driver's `endorsement*expdate` columns and `restriction` child rows (`{restriction, cardtype}`).
 */

export const FieldError = ({ message }: { message?: string }) => (
  message ? <p role="alert" className="col-span-2 -mt-1 text-xs text-danger-fg">{message}</p> : null
);

export interface EndorsementsSectionProps {
  /** The record holding the `endorsement*expdate` columns (cert scalars). */
  values: Record<string, any>;
  onChange: (key: string, value: unknown) => void;
  portalContainer: HTMLElement | null;
  /** Scalar-field errors (`field -> message`), rendered under the matching date. */
  fieldErrors?: Record<string, string>;
}

/**
 * One row per `ENDORSEMENTS` entry: an endorsement is "on" when its expiry is set; ticking
 * the box enables the date before one exists, unticking clears it (posted as `''`).
 * Renders INSIDE a `Section` (it returns the rows, not the fieldset) so callers can append
 * their own fields (ISO tank / reefer approval) to the same grid.
 */
export function EndorsementsSection({ values, onChange, portalContainer, fieldErrors = {} }: EndorsementsSectionProps) {
  const [enabled, setEnabled] = useState<Record<string, boolean>>({});
  const isOn = (field: string) => enabled[field] ?? !isZeroDate(values[field]);
  const toggle = (field: string, on: boolean) => {
    setEnabled((p) => ({ ...p, [field]: on }));
    if (!on) onChange(field, '');
  };
  return (
    <>
      {ENDORSEMENTS.map((e) => {
        const on = isOn(e.field);
        return (
          <div key={e.code} className="col-span-2 grid grid-cols-2 items-end gap-2">
            <label className="flex items-center gap-2 pb-1.5 text-sm">
              <PermissionAwareInput type="checkbox" aria-label={`${e.label} endorsement`} className="h-4 w-4 rounded border-rule-strong"
                checked={on} onChange={(ev) => toggle(e.field, ev.target.checked)} />
              <span>{e.code} — {e.label}</span>
            </label>
            <DateField label={`${e.label} Exp`} value={values[e.field]} onChange={(v) => onChange(e.field, v)} portalContainer={portalContainer} readOnly={!on} />
            <FieldError message={fieldErrors[e.field]} />
          </div>
        );
      })}
    </>
  );
}

const CARD_GRIDS: { cardtype: string; title: string; label: string }[] = [
  { cardtype: 'DRIVERS LICENSE', title: 'Drivers License Restrictions', label: 'DL Restriction' },
  { cardtype: 'MEDICAL', title: 'Medical Restrictions', label: 'Medical Restriction' },
];

export interface RestrictionGridsProps {
  rows: ChildRow[];
  onRowsChange: (rows: ChildRow[]) => void;
  restrictiontypes: RestrictionTypeLookup[] | undefined;
  /** Row factory for "Add … restriction" (seeds `cardtype` for the grid being added to). */
  newRow: (cardtype: string) => ChildRow;
  /** `UnitChildGrid` row errors (`index -> field -> message`). */
  errors?: Record<string, Record<string, string>>;
}

/** One `UnitChildGrid` per card type over the same restriction list, each filtered to its type. */
export function RestrictionGrids({ rows, onRowsChange, restrictiontypes, newRow, errors }: RestrictionGridsProps) {
  return (
    <>
      {CARD_GRIDS.map((g) => (
        <UnitChildGrid
          key={g.cardtype}
          title={g.title}
          rows={rows}
          onRowsChange={onRowsChange}
          filter={(r) => str(r.cardtype).toUpperCase() === g.cardtype}
          columns={[
            {
              key: 'restriction', label: g.label, type: 'select', className: 'min-w-[12rem]',
              options: (restrictiontypes ?? []).filter((t) => str(t.cardtype).toUpperCase() === g.cardtype)
                .map((t) => ({ value: String(t.recno), label: `${t.restrictioncode} - ${t.type}` })),
            },
          ]}
          newRow={() => newRow(g.cardtype)}
          addLabel={`Add ${g.cardtype === 'MEDICAL' ? 'medical' : 'DL'} restriction`}
          removeLabel={`Remove ${g.label}`}
          emptyText={`No ${g.cardtype.toLowerCase()} restrictions.`}
          errors={errors}
        />
      ))}
    </>
  );
}
